import {
	type ArcadeId,
	type BestOf,
	CLOSE_TAKEN_OVER,
	type Difficulty,
	type GameId,
	type InputEvent,
	PING_FRAME,
	type PlayerIndex,
	type Reaction,
	type RoomView,
	type ServerMessage,
	type TurnSeconds,
} from "@playpal/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { socketUrl } from "./config";
import type { Identity } from "./identity";

export interface IncomingReaction {
	id: number;
	seat: PlayerIndex;
	emoji: Reaction;
}

export type ConnectionStatus = "connecting" | "playing" | "reconnecting" | "closed";

export interface RoomConnection {
	status: ConnectionStatus;
	room: RoomView | null;
	seat: PlayerIndex | null;
	/** Set when the room cannot be joined at all — a wrong code, or a full room. */
	fatal: string | null;
	/** Transient "you can't do that" nudge from the server. */
	nudge: string | null;
	clearNudge: () => void;
	/** The server's clock minus ours, so timers agree across devices. */
	clockOffset: number;
	pick: (gameId: GameId, options?: { bestOf?: BestOf; turnSeconds?: TurnSeconds }) => void;
	/** Starts an arcade race. The server picks the seed both players will use. */
	startArcade: (arcadeId: ArcadeId) => void;
	/**
	 * Sends up a finished run — the list of presses, and nothing else.
	 *
	 * There is deliberately no score in this call. The server replays the log
	 * and works one out, so the only number that ever reaches a leaderboard is
	 * one it computed itself.
	 *
	 * Kept and re-sent on every reconnection until `runLanded` says it arrived.
	 * Everything else here can be dropped without harm — a reaction, a nudge, a
	 * request for a game — but a run is minutes of a child's evening, and the
	 * socket drops whatever it is handed while it is not open.
	 */
	sendRun: (log: readonly InputEvent<unknown>[], seed: number) => void;
	/** Stops re-sending: the score came back. */
	runLanded: () => void;
	move: (move: unknown) => void;
	react: (emoji: Reaction) => void;
	again: () => void;
	backToLobby: () => void;
	/** Change how hard the computer plays, without leaving the room. */
	setDifficulty: (difficulty: Difficulty) => void;
	/** Takes the win when the other player has been gone long enough. */
	claim: () => void;
	/** Reactions that have arrived recently, newest last. */
	reactions: IncomingReaction[];
}

const PING_EVERY_MS = 25_000;
const RECONNECT_DELAYS_MS = [500, 1000, 2000, 4000, 8000];

/**
 * Keeps one WebSocket open to a room and mirrors whatever the server says. The
 * client never decides anything about the game — it asks, and re-renders.
 */
export function useRoom(
	code: string | null,
	identity: Identity,
	token: string | null = null,
): RoomConnection {
	const [status, setStatus] = useState<ConnectionStatus>("connecting");
	const [room, setRoom] = useState<RoomView | null>(null);
	const [seat, setSeat] = useState<PlayerIndex | null>(null);
	const [fatal, setFatal] = useState<string | null>(null);
	const [nudge, setNudge] = useState<string | null>(null);
	const [clockOffset, setClockOffset] = useState(0);
	const [reactions, setReactions] = useState<IncomingReaction[]>([]);

	const socketRef = useRef<WebSocket | null>(null);
	/*
	 * The one message worth keeping hold of.
	 *
	 * A run is only known to have arrived when the child's own score comes back
	 * in a room broadcast, and `send` below silently does nothing when the
	 * socket is not open — so a run offered up during a reconnection simply
	 * vanished, and the child was left on "sending your score" for ever. It now
	 * goes out again as soon as there is a socket to put it on.
	 *
	 * The seed it was recorded against travels with it, because a kept log is
	 * only meaningful in the race it was played in. Without it, a run abandoned
	 * on the way to the lobby sat here until the next reconnection and was then
	 * replayed into whatever race happened to be running — the server would
	 * verify it against the new seed and record a garbage run for this seat.
	 */
	const runOutbox = useRef<{ log: readonly InputEvent<unknown>[]; seed: number } | null>(null);
	const attemptRef = useRef(0);
	const identityRef = useRef(identity);
	identityRef.current = identity;
	const tokenRef = useRef(token);
	tokenRef.current = token;
	const reactionId = useRef(0);

	// biome-ignore lint/correctness/useExhaustiveDependencies: token is read through a ref, but signing in mid-session must reconnect so the server re-seats you.
	useEffect(() => {
		/*
		 * A new code is a new room, and nothing carried over from the last one
		 * can be trusted. Without this, leaving room A and joining room B
		 * rendered room A in full — old code, old players, old board — until
		 * B's welcome arrived, and the "remember the last room" hook re-wrote
		 * A's code to storage during the window.
		 *
		 * Clearing `fatal` here matters twice over: it is also what makes a
		 * second wrong code in a row work. The server's refusal is a fixed
		 * string, and setting state to the identical string is an update React
		 * bails on — so the screen watching `fatal` never heard about the
		 * second mistake and the child sat on "Joining" for ever. Going through
		 * null on every attempt means the next refusal is always a change.
		 */
		setRoom(null);
		setSeat(null);
		setFatal(null);
		setNudge(null);
		setReactions([]);
		attemptRef.current = 0;

		if (!code) return;

		let disposed = false;
		let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
		let pingTimer: ReturnType<typeof setInterval> | undefined;

		const connect = () => {
			if (disposed) return;
			setStatus(attemptRef.current === 0 ? "connecting" : "reconnecting");

			const socket = new WebSocket(socketUrl(`/api/rooms/${code}/socket`));
			socketRef.current = socket;

			socket.addEventListener("open", () => {
				attemptRef.current = 0;
				const { playerId, name, avatar } = identityRef.current;
				socket.send(
					JSON.stringify({ type: "join", playerId, name, avatar, token: tokenRef.current }),
				);
				// A kept run goes back up from the `welcome` handler, not here:
				// only the welcome says which race the room is now showing, and
				// a run must never be replayed against a seed it was not played
				// on.
				pingTimer = setInterval(() => {
					if (socket.readyState === WebSocket.OPEN) socket.send(PING_FRAME);
				}, PING_EVERY_MS);
			});

			socket.addEventListener("message", (event) => {
				if (typeof event.data !== "string") return;
				let message: ServerMessage;
				try {
					message = JSON.parse(event.data) as ServerMessage;
				} catch {
					return;
				}

				/*
				 * A kept run either matches the race the room is showing, or it
				 * is stale and must go. "Stale" is any room where the arcade is
				 * over or a different one has started — back to the lobby, a
				 * new seed, a different room entirely. Dropping it here is what
				 * stops a log recorded against seed A being replayed into a
				 * later race with seed B.
				 */
				const reconcileOutbox = (arcade: { seed: number } | null) => {
					if (runOutbox.current && runOutbox.current.seed !== arcade?.seed) {
						runOutbox.current = null;
					}
				};

				switch (message.type) {
					case "welcome":
						setSeat(message.seat);
						setRoom(message.room);
						setClockOffset(message.room.serverNow - Date.now());
						setStatus("playing");
						setFatal(null);
						reconcileOutbox(message.room.arcade);
						// After the join, so the room knows whose seat this run
						// belongs to — and only for the race it was recorded
						// in. Harmless if the room already has it: a second
						// copy is answered with the room rather than a refusal.
						if (runOutbox.current) {
							socket.send(JSON.stringify({ type: "run", log: runOutbox.current.log }));
						}
						break;
					case "room":
						setRoom(message.room);
						setClockOffset(message.room.serverNow - Date.now());
						setStatus("playing");
						reconcileOutbox(message.room.arcade);
						break;
					case "error":
						if (message.code === "room-not-found" || message.code === "room-full") {
							setFatal(message.message);
							disposed = true;
							socket.close();
						} else {
							setNudge(message.message);
						}
						break;
					case "reaction": {
						const id = ++reactionId.current;
						setReactions((current) => [
							...current,
							{ id, seat: message.seat, emoji: message.emoji },
						]);
						// They float for a moment and then they are gone.
						setTimeout(() => setReactions((current) => current.filter((r) => r.id !== id)), 2600);
						break;
					}
					case "pong":
						break;
				}
			});

			socket.addEventListener("close", (event) => {
				clearInterval(pingTimer);
				if (disposed) {
					setStatus("closed");
					return;
				}
				/*
				 * The seat has been taken from somewhere else — a second tab, or
				 * the same link opened twice.
				 *
				 * Reconnecting here would take it straight back, the other tab
				 * would take it again, and the two would trade it several times a
				 * second for as long as both were open: a permanent "reconnecting"
				 * banner in both, and the per-address socket budget gone in half a
				 * minute. The newest window keeps the game; this one says so and
				 * goes back to the front page.
				 */
				if ((event as CloseEvent).code === CLOSE_TAKEN_OVER) {
					disposed = true;
					setFatal("You opened this game in another window.");
					setStatus("closed");
					return;
				}
				const delay =
					RECONNECT_DELAYS_MS[Math.min(attemptRef.current, RECONNECT_DELAYS_MS.length - 1)];
				attemptRef.current += 1;
				setStatus("reconnecting");
				reconnectTimer = setTimeout(connect, delay);
			});
		};

		connect();

		return () => {
			disposed = true;
			clearTimeout(reconnectTimer);
			clearInterval(pingTimer);
			socketRef.current?.close();
			socketRef.current = null;
		};
	}, [code, token]);

	const send = useCallback((payload: unknown) => {
		const socket = socketRef.current;
		if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(payload));
	}, []);

	return {
		status,
		room,
		seat,
		fatal,
		nudge,
		clockOffset,
		clearNudge: useCallback(() => setNudge(null), []),
		reactions,
		pick: useCallback(
			(gameId: GameId, options?: { bestOf?: BestOf; turnSeconds?: TurnSeconds }) =>
				send({
					type: "pick",
					gameId,
					bestOf: options?.bestOf ?? 1,
					turnSeconds: options?.turnSeconds ?? 0,
				}),
			[send],
		),
		startArcade: useCallback((arcadeId: ArcadeId) => send({ type: "arcade", arcadeId }), [send]),
		sendRun: useCallback(
			(log: readonly InputEvent<unknown>[], seed: number) => {
				runOutbox.current = { log, seed };
				send({ type: "run", log });
			},
			[send],
		),
		runLanded: useCallback(() => {
			runOutbox.current = null;
		}, []),
		move: useCallback((move: unknown) => send({ type: "move", move }), [send]),
		react: useCallback((emoji: Reaction) => send({ type: "react", emoji }), [send]),
		again: useCallback(() => send({ type: "again" }), [send]),
		backToLobby: useCallback(() => send({ type: "lobby" }), [send]),
		setDifficulty: useCallback(
			(difficulty: Difficulty) => send({ type: "difficulty", difficulty }),
			[send],
		),
		claim: useCallback(() => send({ type: "claim" }), [send]),
	};
}
